/**
 * Constrains the position of an inner rectangle so it always stays inside of the bounding box.
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 * @param {number} newX - Proposed X coordinate for the inner rectangle’s center.
 * @param {number} newY - Proposed Y coordinate for the inner rectangle’s center.
 *
 * @returns {fabric.Point} A corrected point (X, Y) that keeps the inner rectangle fully inside the bounding box.
 */
export const constrainPositionToBoundingBox = function (boundingBox, innerRect, newX, newY) {
    // Get the center and half-dimensions of the bounding box.
    const center = new fabric.Point(boundingBox.left, boundingBox.top);
    const halfWidth = boundingBox.width / 2;
    const halfHeight = boundingBox.height / 2;

    // Create a point from the given (x, y).
    let point = new fabric.Point(newX, newY);

    // "Unrotate" the point so the bounding box is axis-aligned.
    point = fabric.util.rotatePoint(point, center, fabric.util.degreesToRadians(-boundingBox.angle));

    // Find the relative angle between the inner rectangle and the bounding box.
    const relativeAngle = fabric.util.degreesToRadians(innerRect.angle - boundingBox.angle);

    // Compute the effective half-width and half-height of the inner rectangle.
    const effectiveHalfWidth =
        (Math.abs(innerRect.width * Math.cos(relativeAngle)) +
            Math.abs(innerRect.height * Math.sin(relativeAngle))) /
        2;
    const effectiveHalfHeight =
        (Math.abs(innerRect.width * Math.sin(relativeAngle)) +
            Math.abs(innerRect.height * Math.cos(relativeAngle))) /
        2;

    // Determine the allowed range for the inner rectangle's center.
    const minX = center.x - halfWidth + effectiveHalfWidth;
    const maxX = center.x + halfWidth - effectiveHalfWidth;
    const minY = center.y - halfHeight + effectiveHalfHeight;
    const maxY = center.y + halfHeight - effectiveHalfHeight;

    // Clamp the point within these allowed ranges.
    point.x = Math.min(Math.max(point.x, minX), maxX);
    point.y = Math.min(Math.max(point.y, minY), maxY);

    // Rotate the point back to the original coordinate system.
    return fabric.util.rotatePoint(point, center, fabric.util.degreesToRadians(boundingBox.angle));
};

/**
 * Calculates how much an inner rectangle can be scaled so it always fits inside of the bounding box
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 * @param {number|null} [innerRectWidth=null] - Optional width to use instead of `innerRect.width`.
 * @param {number|null} [innerRectHeight=null] - Optional height to use instead of `innerRect.height`.
 *
 * @returns {object} An object with:
 *   - `scaleX`: Maximum horizontal scale factor allowed.
 *   - `scaleY`: Maximum vertical scale factor allowed.
 *   - `maxScale`: The limiting scale factor (minimum of `scaleX` and `scaleY`).
 *   - `width`: Maximum width that fits inside the bounding box.
 *   - `height`: Maximum height that fits inside the bounding box.
 */
export const constrainDimensionToBoundingBox = function (boundingBox, innerRect, innerRectWidth = null, innerRectHeight = null) {
    const bbAngleRad = fabric.util.degreesToRadians(boundingBox.angle);

    // Get the inner rectangle's center position in the coordinate system of the bounding box.
    // This is done by shifting its center relative to the bounding box and then rotating accordingly.
    const dx = innerRect.left - boundingBox.left;
    const dy = innerRect.top - boundingBox.top;
    const cosBB = Math.cos(-bbAngleRad);
    const sinBB = Math.sin(-bbAngleRad);
    const localX = dx * cosBB - dy * sinBB;
    const localY = dx * sinBB + dy * cosBB;

    // Determine how much space is available from the inner rectangle's center to the edges of the bounding box.
    const halfBBWidth = boundingBox.width / 2;
    const halfBBHeight = boundingBox.height / 2;
    const availX = halfBBWidth - Math.abs(localX);
    const availY = halfBBHeight - Math.abs(localY);

    // Find out how the inner rectangle is rotated relative to the bounding box.
    const innerAngleRad = fabric.util.degreesToRadians(innerRect.angle);
    const relativeAngle = innerAngleRad - bbAngleRad;
    const absCos = Math.abs(Math.cos(relativeAngle));
    const absSin = Math.abs(Math.sin(relativeAngle));

    // Get half the inner rectangle's dimensions.
    let width = innerRectWidth ? innerRectWidth : innerRect.width;
    let height = innerRectHeight ? innerRectHeight : innerRect.height;

    const halfW = width / 2;
    const halfH = height / 2;

    // Calculate the largest scale factor that keeps the inner rectangle fully inside the bounding box.
    // This factor is limited by both the horizontal and vertical available space.
    const sX = availX / (halfW * absCos + halfH * absSin);
    const sY = availY / (halfW * absSin + halfH * absCos);

    // Compute the maximum width and height after applying the scale factor.
    const maxWidth = width * sX;
    const maxHeight = height * sY;

    // Return the computed scale factor and the new dimensions.
    return {
        scaleX: sX,
        scaleY: sY,
        maxScale: Math.min(sX, sY),
        width: maxWidth,
        height: maxHeight
    };
};

/**
 * Calculates how much the inner rectangle can rotate (clockwise and counter-clockwise) while still staying fully inside of the bounding box.
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 *
 * @returns {object} An object with:
 *   - `minRotationDelta`: Maximum allowed counter-clockwise rotation (in degrees, negative value).
 *   - `maxRotationDelta`: Maximum allowed clockwise rotation (in degrees, positive value).
 */
export const constrainRotationToBoundingBox = function (boundingBox, innerRect) {
    // Get the center and half-dimensions of the bounding box.
    const center = new fabric.Point(boundingBox.left, boundingBox.top);
    const halfWidth = boundingBox.width / 2;
    const halfHeight = boundingBox.height / 2;

    // Convert the innerRect's center into the bounding box coordinate system.
    const rectCenterInBB = fabric.util.rotatePoint(
        new fabric.Point(innerRect.left, innerRect.top),
        center,
        fabric.util.degreesToRadians(-boundingBox.angle)
    );

    // Compute available margins: the distance from the innerRect's center to the edges of the bounding box.
    const availX = halfWidth - Math.abs(rectCenterInBB.x - center.x);
    const availY = halfHeight - Math.abs(rectCenterInBB.y - center.y);

    // Get the base dimensions of the innerRect.
    const w = innerRect.width;
    const h = innerRect.height;

    // Calculate the current relative angle (in radians) of innerRect in the bounding box system.
    let currentRel = fabric.util.degreesToRadians(innerRect.angle - boundingBox.angle);

    // Function to check if innerRect fits inside the bounding box for a given relative angle.
    function isValid(theta) {
        const effectiveHalfWidth = (w * Math.abs(Math.cos(theta)) + h * Math.abs(Math.sin(theta))) / 2;
        const effectiveHalfHeight = (w * Math.abs(Math.sin(theta)) + h * Math.abs(Math.cos(theta))) / 2;
        return effectiveHalfWidth <= availX && effectiveHalfHeight <= availY;
    }

    // Incrementally find the maximum allowed delta (in radians) in both directions.
    const step = fabric.util.degreesToRadians(0.5); // 0.5° step
    let maxDeltaPositive = 0; // Maximum increase allowed
    let maxDeltaNegative = 0; // Maximum decrease allowed

    // Check for positive delta.
    let theta = currentRel;
    while (isValid(theta)) {
        maxDeltaPositive = theta - currentRel;
        theta += step;
        if (theta - currentRel > Math.PI * 2) break; // Prevent infinite loop
    }

    // Check for negative delta.
    theta = currentRel;
    while (isValid(theta)) {
        maxDeltaNegative = currentRel - theta;
        theta -= step;
        if (currentRel - theta > Math.PI * 2) break;
    }

    // Return the rotation deltas in degrees.
    return {
        minRotationDelta: -fabric.util.radiansToDegrees(maxDeltaNegative),
        maxRotationDelta: fabric.util.radiansToDegrees(maxDeltaPositive)
    };
};